import { checkTableName } from "../../../../../api/bills";
import type { TableReservationNotice } from "../../../../../api/bills";

/**
 * Отговорил ли е ИЗОБЩО облакът. Мрежова грешка (спрял интернет, таймаут,
 * DNS) няма `response` — тогава съобщението е за връзката, а не за масата.
 * Отговорил 4xx носи `detail` от BE-то и то се показва дословно.
 */
export const hasApiDetail = (error: unknown): boolean => {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return false;
  }
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    .response?.data?.detail;
  return detail !== undefined && detail !== null && detail !== "";
};

/**
 * Счупена ли е САМАТА проверка, при жива мрежа: 404 (маршрутът го няма — облак
 * или каса отпреди него) и 5xx (гръмнал е вътре). Само тези два отговора значат
 * „питахме и не получихме отговор на въпроса"; всичко друго е или мълчаща
 * мрежа, или отговор по същество, който трябва да се покаже.
 */
const theCheckRouteItselfIsBroken = (error: unknown): boolean => {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === "number" && (status === 404 || status >= 500);
};

/** Какво да се каже в скобите на предупреждението — кодът, ако го има. */
const reasonOf = (error: unknown): string => {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === "number" ? String(status) : "";
};

export type NewTableNameOutcome =
  // Масата се отваря; `skippedReason` ≠ null — проверката беше счупена
  // (404/5xx) и се продължава с предупреждение.
  | { kind: "open"; skippedReason: string | null }
  // `takenBy` — сервитьорът на заетата маса (без сумата); null — облакът не го е казал.
  | { kind: "exists"; takenBy: string | null }
  // „Отказ" на „Масата има резервация — продължаваш ли?"
  | { kind: "declined" }
  | { kind: "failed"; error: unknown };

/**
 * Проверката преди „Нова маса" — общата за модала с името (`NewTableModal`)
 * и за свободна маса от схемата (режим „Схема"): заето име → отказ;
 * резервация на масата → „Продължаваш ли?" (`confirmNotice`); счупена проверка
 * (404/5xx) → отваря с предупреждение, защото заетото име се отказва и от
 * самото създаване — и в облака, и на касата (инвариант 15: при съмнение
 * ОБСЛУЖВАЙ); мрежова грешка и всеки друг отговор → отказ, който се показва.
 * `onAnswered` се вика преди въпроса за резервацията (спира въртележката).
 */
export const checkNewTableName = async (
  name: string,
  confirmNotice: (name: string, notice: TableReservationNotice) => Promise<boolean>,
  onAnswered?: () => void,
  // Нова маса на друг сервитьор (изборът на сервитьор преди „Нова маса") — при лична
  // номерация заетото име се търси сред неговите маси.
  waiterId?: number,
): Promise<NewTableNameOutcome> => {
  try {
    const { exists, reservation, takenBy } = await checkTableName(name, undefined, waiterId);
    if (exists) return { kind: "exists", takenBy };
    // Масата е заета от резервация (настанена на съседна маса / предстояща
    // скоро) — модал „Продължаваш ли?".
    if (reservation) {
      onAnswered?.();
      if (!(await confirmNotice(name, reservation))) return { kind: "declined" };
    }
    return { kind: "open", skippedReason: null };
  } catch (error) {
    if (theCheckRouteItselfIsBroken(error)) {
      return { kind: "open", skippedReason: reasonOf(error) };
    }
    return { kind: "failed", error };
  }
};
